/**
 * Pinch zoom in place for product photos and personalize previews on touch screens, as on
 * Teeinblue stores: two fingers zoom the photo inside its frame (1x to 4x, around the point between
 * the fingers) and move it with them; on release it eases back to 1x. One finger still swipes the
 * gallery and scrolls the page. The page itself does not zoom (snippets/meta-tags, ym-head).
 *
 * ZONES maps a frame (it clips, overflow: hidden) to what zooms inside it, so badges, counters and
 * view pills stay put. Teeinblue's preview is zoomed here too: with its "Zoom gallery" setting the
 * app has a pinch zoom of its own on Android, but one that stays zoomed after release; this script
 * takes the two-finger touches first (capture phase) and keeps them from it.
 *
 * After a pinch, a finger still down neither scrolls nor reaches other scripts until all are up, so
 * it does not swipe the gallery or the app's mockups to another photo.
 */
(function () {
  if (window.ymPinchZoom) return;
  window.ymPinchZoom = true;

  const ZONES = [
    // Teeinblue gallery (popup or page): the mockup in the slide shown
    { frame: '.tee-slide', zoom: ':scope > *' },
    // Product page gallery: the photo, not the Bestseller badge
    { frame: '.ym-gal__slide', zoom: ':scope > img' },
    // Yallmark personalize popup: the pieces drawn in the grid, not the view and colour pills
    { frame: '[data-ym-pz-grid]', zoom: ':scope > *' },
    // Easify in the Yallmark popup: the slides the app draws into the stage
    { frame: '[data-ym-app-preview]', zoom: ':scope > *' },
  ];
  const MAX = 4;
  const EASE = 'transform 0.25s ease';

  /** @type {{ frame: Element, items: { el: HTMLElement, left: number, top: number }[], d0: number, x0: number, y0: number } | null} */
  let pinch = null;
  // A pinch just ended and a finger is still down.
  let settling = false;

  /** @param {EventTarget | null} target */
  function zoneOf(target) {
    if (!(target instanceof Element)) return null;
    for (const zone of ZONES) {
      const frame = target.closest(zone.frame);
      if (frame) return { frame, zoom: zone.zoom };
    }
    return null;
  }

  /** @param {TouchList} t */
  function span(t) {
    return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  }

  /** @param {TouchList} t */
  function middle(t) {
    return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 };
  }

  /** @param {TouchEvent} event */
  function start(event) {
    if (event.touches.length !== 2 || pinch) return;
    const a = zoneOf(event.touches[0].target);
    const b = zoneOf(event.touches[1].target);
    if (!a || !b || a.frame !== b.frame) return;
    const items = [...a.frame.querySelectorAll(a.zoom)]
      .filter((el) => el instanceof HTMLElement && !el.hidden)
      .map((el) => {
        const box = el.getBoundingClientRect();
        el.style.transition = 'none';
        el.style.transformOrigin = '0 0';
        el.style.willChange = 'transform';
        return { el: /** @type {HTMLElement} */ (el), left: box.left, top: box.top };
      });
    if (!items.length) return;
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    const m = middle(event.touches);
    pinch = { frame: a.frame, items, d0: span(event.touches) || 1, x0: m.x, y0: m.y };
  }

  /** @param {TouchEvent} event */
  function move(event) {
    if (!pinch && !settling) return;
    // Not cancelable when the first finger already started scrolling the gallery strip.
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    if (!pinch || event.touches.length < 2) return;
    const s = Math.min(MAX, Math.max(1, span(event.touches) / pinch.d0));
    const m = middle(event.touches);
    // The point first under the fingers stays under them: translate = now - start * scale.
    for (const item of pinch.items) {
      const x = m.x - item.left - (pinch.x0 - item.left) * s;
      const y = m.y - item.top - (pinch.y0 - item.top) * s;
      item.el.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
    }
  }

  /** @param {TouchEvent} event */
  function end(event) {
    settling = event.touches.length > 0 && (settling || Boolean(pinch));
    if (!pinch || event.touches.length >= 2) return;
    const items = pinch.items;
    for (const { el } of items) {
      el.style.transition = EASE;
      el.style.transform = '';
    }
    pinch = null;
    window.setTimeout(() => {
      // A new pinch may have started on the same photo meanwhile.
      if (pinch?.items.some((item) => items.some((done) => done.el === item.el))) return;
      for (const { el } of items) {
        el.style.transition = '';
        el.style.transformOrigin = '';
        el.style.willChange = '';
      }
    }, 300);
  }

  // Capture phase: ahead of the apps' own touch handlers, which stopPropagation then keeps out.
  document.addEventListener('touchstart', start, { passive: false, capture: true });
  document.addEventListener('touchmove', move, { passive: false, capture: true });
  document.addEventListener('touchend', end, true);
  document.addEventListener('touchcancel', end, true);
})();
